import { combineReducers } from "redux";
import { keepBrandAssetsOnTop } from "./keepBrandAssetsOnTop";
import undoable from "redux-undo";
import {
  ADD_BOX,
  SELECT_BOX,
  INCREASE_LEVEL,
  INDEX_SELECT,
  SELECT_NAV_ITEM,
  EXPAND_NAV_ITEM,
  TOGGLE_TEXT_EDITOR,
  DELETE_RICH_MARK,
  ADD_RICH_MARK,
  ADD_NAV_ITEM,
  ADD_NAV_ITEMS,
  DELETE_CONTAINED_VIEW,
  DELETE_NAV_ITEM,
  SET_BUSY,
  IMPORT_STATE,
  FETCH_VISH_RESOURCES_SUCCESS,
  UPDATE_BOX,
  UPLOAD_FILE,
  SELECT_CONTAINED_VIEW,
  DELETE_FILE,
  CHANGE_GLOBAL_CONFIG,
  IMPORT_EDI,
  UPLOAD_TEMPLATE,
  DELETE_TEMPLATE,
  ADD_SOUND,
  EDIT_SOUND,
  DELETE_SOUND,
  SET_SCENE_AUDIO_ROW_ORDERS,
  BATCH_UPDATE_NARRATION_VOICE,
  ADD_ANIMATION,
  SAVE_ANIMATION,
  DELETE_ANIMATION,
  CHANGE_ANIMATION_DURATIONS,
  UPDATE_TEXT_ANIMATION,
  UPDATE_BOX_ANIMATION,
  DELETE_TEXT_ANIMATION,
  DELETE_BOX_ANIMATION,
} from "../common/actions";
import { isSortableBox } from "../common/utils";
import { sceneDefaultsFromGlobalConfig } from "../common/sceneDefaults.es6";
import boxesById from "./boxes_by_id";
import boxLevelSelected from "./box_level_selected";
import boxSelected from "./box_selected";
import boxSelectedAll from "./box_selected_all";
import containedViewsById from "./contained_views_by_id";
import containedViewSelected from "./contained_view_selected";
import indexSelected from "./index_selected";
import navItemsById from "./nav_items_by_id";
import navItemsIds from "./nav_items_ids";
import navItemSelected from "./nav_item_selected";
import marksById from "./marks_by_id";
import pluginToolbarsById from "./plugin_toolbars_by_id";
import viewToolbarsById from "./view_toolbars_by_id";
import globalConfig from "./global_config";
import guideTagValues from "./guide_tag_values";
import exercises from "./exercises";
import lastActionDispatched from "./lastActionDispatched";
import aideaSmartElementsProcessing from "./aidea_smart_elements_processing.es6";
// === Task 37 ===
import workflowSuggestions from "./workflow_suggestions.es6";
// === /Task 37 ===
// === Interactivity Studio (#4) Task 1 ===
import interactivityStudio from "./interactivity_studio.es6";
// === /Interactivity Studio Task 1 ===
// === Phase B Task 4 — docked Workflow panel ===
import canvasWorkflow from "./canvas_workflow.es6";
// === /Phase B Task 4 ===
// === Phase C item 6 — canvas preview frame ===
import canvasPreview from "./canvas_preview.es6";
// === /Phase C item 6 ===
// === Save-as-template — user-defined Interactive templates ===
import userTemplates from "./user_templates.es6";
// === /Save-as-template ===
import { DUPLICATE_NAV_ITEM_BY_PAYLOAD } from "../common/actions.es6";
import { DUPLICATE_NAV_ITEM } from "../common/actions.es6";
import { PASTE_SCENE } from "../common/actions.es6";
const version = "3";

function changeDisplayMode(state = "", action = {}) {
  switch (action.type) {
    // case CHANGE_DISPLAY_MODE:
    //     return action.payload.mode;
    case IMPORT_STATE:
      return action.payload.present.displayMode || state;
    default:
      return state;
  }
}

function isBusy(state = "", action = {}) {
  switch (action.type) {
    case SET_BUSY:
      return action.payload;
    case IMPORT_STATE:
      return /* action.payload.present.isBusy ||*/ state;
    default:
      return state;
  }
}

function filesUploaded(state = {}, action = {}) {
  switch (action.type) {
    case UPLOAD_FILE:
      return { ...state, [action.payload.id]: { ...action.payload } };
    case DELETE_FILE:
      return Object.keys(state)
        .filter((key) => key !== action.payload.id)
        .reduce((result, current) => {
          result[current] = state[current];
          return result;
        }, {});
    case IMPORT_STATE:
      return action.payload.present.filesUploaded || state;
    case IMPORT_EDI:
      return { ...state, ...action.payload.state.filesUploaded };
    default:
      return state;
  }
}

function uploadTemplate(state = [], action = {}) {
  switch (action.type) {
    case UPLOAD_TEMPLATE:
      return [...(Array.isArray(state) ? state : []), action.payload];
    case DELETE_TEMPLATE:
      return (Array.isArray(state) ? state : []).filter(
        (template) => template.id !== action.payload.id
      );
    case IMPORT_STATE:
      return Array.isArray(action.payload.present.uploadedTemplates)
        ? action.payload.present.uploadedTemplates
        : state;
    default:
      return state;
  }
}

const _soundStartTimeComparator = (a, b) =>
  (a?.startTime ?? 0) < (b?.startTime ?? 0)
    ? -1
    : (a?.startTime ?? 0) > (b?.startTime ?? 0)
      ? 1
      : 0;

// Sounds are addressed by their stable `id` (string key); a numeric key is the
// legacy positional fallback for content created before ids existed.
function _resolveSoundIndex(list, key) {
  if (typeof key === "string") {
    const byId = list.findIndex((s) => s && s.id === key);
    if (byId !== -1) return byId;
    // buildLayers.js falls back to a synthetic `scene-audio-${idx}` Timeline
    // row id for sounds that predate stable ids (`sound.id` missing) — that
    // synthetic id is what row-reorder drags carry as `soundId`, so it has
    // to resolve back to the same positional index here too, or the update
    // silently matches nothing.
    const m = /^scene-audio-(\d+)$/.exec(key);
    if (m) {
      const idx = Number(m[1]);
      if (idx >= 0 && idx < list.length) return idx;
    }
  }
  if (typeof key === "number" && key >= 0 && key < list.length) return key;
  return -1;
}

export function addSound(state = {}, action = {}) {
  switch (action.type) {
    case BATCH_UPDATE_NARRATION_VOICE: {
      const { voiceData } = action.payload;
      const next = {};
      for (const id of Object.keys(state)) {
        const sounds = state[id];
        next[id] = Array.isArray(sounds)
          ? sounds.map((s) => ({ ...s, voiceData: { ...(s.voiceData || {}), ...voiceData } }))
          : sounds;
      }
      return next;
    }
    case ADD_SOUND:
      // Keep the list chronologically sorted on add too — EDIT_SOUND already
      // sorts, and a mixed invariant made array order unpredictable.
      return {
        ...state,
        [action.payload.id]: (state[action.payload.id] !== undefined
          ? state[action.payload.id]
          : []
        )
          .concat(action.payload.value)
          .sort(_soundStartTimeComparator),
      };
    case EDIT_SOUND: {
      const list = state[action.payload.id] || [];
      const idx = _resolveSoundIndex(list, action.payload.key);
      if (idx < 0) return state;
      const stored = list[idx];
      const next = [...list];
      // The stored id always wins so a stale copy can never fork identities.
      next[idx] =
        stored && stored.id ? { ...action.payload.value, id: stored.id } : action.payload.value;
      next.sort(_soundStartTimeComparator);
      return { ...state, [action.payload.id]: next };
    }
    case DELETE_SOUND: {
      const list = state[action.payload.id] || [];
      const idx = _resolveSoundIndex(list, action.payload.key);
      if (idx < 0) return state;
      return { ...state, [action.payload.id]: list.filter((_, i) => i !== idx) };
    }
    case SET_SCENE_AUDIO_ROW_ORDERS: {
      const { sceneId, updates } = action.payload;
      const list = state[sceneId];
      if (!Array.isArray(list) || !Array.isArray(updates) || updates.length === 0) return state;
      // Only touches the display-order field on the affected sounds — the
      // array itself stays in whatever chronological (startTime) order
      // ADD_SOUND/EDIT_SOUND already sorted it into, so nothing that reads
      // soundList[0]/array order for "the earliest narration" (e.g.
      // AvatarNarrationTab) is affected.
      const next = [...list];
      let changed = false;
      updates.forEach(({ soundId, rowOrder }) => {
        const idx = _resolveSoundIndex(next, soundId);
        if (idx < 0) return;
        next[idx] = { ...next[idx], rowOrder };
        changed = true;
      });
      return changed ? { ...state, [sceneId]: next } : state;
    }
    case DELETE_NAV_ITEM: {
      const newState = { ...state };
      const idsToRemove = action.payload.ids ? action.payload.ids : [];
      idsToRemove.forEach((id) => {
        delete newState[id];
      });
      return newState;
    }
    case DELETE_CONTAINED_VIEW: {
      const newState = { ...state };
      const idsToRemove = action.payload.ids ? action.payload.ids : [];
      idsToRemove.forEach((id) => {
        delete newState[id];
      });
      return newState;
    }
    case IMPORT_STATE:
      return action.payload.present.soundList || state;
    case DUPLICATE_NAV_ITEM:
      return {
        ...state,
        [action.payload.newId]: action.payload.soundList || state[action.payload.id] || [],
      };
    case DUPLICATE_NAV_ITEM_BY_PAYLOAD:
      return { ...state, [action.payload.newId]: action.payload.soundList };
    case PASTE_SCENE:
      return { ...state, [action.payload.newSceneId]: action.payload.soundList };
    default:
      return state;
  }
}

function addAnimation(state = {}, action = {}) {
  switch (action.type) {
    case ADD_ANIMATION:
      const currentAnimations = state[action.payload.id] || [];
      const newAnimation = action.payload.animation;

      if (!newAnimation || typeof newAnimation !== "object") {
        return state;
      }

      return {
        ...state,
        [action.payload.id]: [...currentAnimations, newAnimation].sort((a, b) => {
          const timeA = typeof a.duration === "number" ? a.duration : 0;
          const timeB = typeof b.duration === "number" ? b.duration : 0;
          return timeA - timeB;
        }),
      };
    case SAVE_ANIMATION:
      if (!action.payload.animations || !Array.isArray(action.payload.animations)) {
        return state;
      }
      return {
        ...state,
        [action.payload.id]: action.payload.animations
          .map((anim) => {
            const duration =
              typeof anim.duration === "number" ? anim.duration : parseFloat(anim.duration) || 0;
            return {
              ...anim,
              duration: Math.max(0, duration),
              id: anim.id || `anim_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            };
          })
          .sort((a, b) => a.duration - b.duration),
      };
    case DELETE_ANIMATION:
      return {
        ...state,
        [action.payload.id]: [],
      };
    case IMPORT_STATE:
      return action.payload.present.animationsById || state;
    case DUPLICATE_NAV_ITEM:
      var animobj = {};
      Object.keys(action.payload.boxes).map((key) => {
        if (state[key] !== undefined) {
          animobj[action.payload.boxes[key]] = state[key];
        }
      });
      return { ...state, ...animobj };
    case DUPLICATE_NAV_ITEM_BY_PAYLOAD:
      var animobjbypayload = {};
      action.payload.boxes.map((box) => {
        if (action.payload.animationList.find((r) => r.id == box.cloneId) != null) {
          animobjbypayload[box.id] = action.payload.animationList.find(
            (r) => r.id == box.cloneId
          ).animations;
        }
      });
      return { ...state, ...animobjbypayload };
    case PASTE_SCENE:
      return { ...state, ...action.payload.animations };
    case CHANGE_ANIMATION_DURATIONS:
      const animations = state[action.payload.id];
      if (!animations || !Array.isArray(animations)) {
        return state;
      }
      return {
        ...state,
        [action.payload.id]: animations
          .map((anim, index) => {
            const updatedAnim = { ...anim };
            if (index === 0 && typeof action.payload.firstduration === "number") {
              updatedAnim.duration = Math.max(0.1, action.payload.firstduration);
            } else if (
              index === animations.length - 1 &&
              typeof action.payload.lastduration === "number"
            ) {
              updatedAnim.duration = Math.max(0.1, action.payload.lastduration);
            }
            return updatedAnim;
          })
          .sort((a, b) => a.duration - b.duration),
      };

    default:
      return state;
  }
}

function versionReducer(state = {}, action = {}) {
  return version;
}

function status(state = "draft", action = {}) {
  switch (action.type) {
    case CHANGE_GLOBAL_CONFIG:
      if (action.payload.prop === "STATE") {
        return action.payload.value.status;
      } else if (action.payload.prop === "status") {
        return action.payload.value;
      }
      return state;
    case IMPORT_STATE:
      return action.payload.present.status || state;
    default:
      return state;
  }
}
function everPublished(state = false, action = {}) {
  switch (action.type) {
    case CHANGE_GLOBAL_CONFIG:
      if (
        action.payload.prop === "STATE" &&
        action.payload.value.status === "final" &&
        state === false
      ) {
        return true;
      } else if (
        action.payload.prop === "status" &&
        action.payload.value === "final" &&
        state === false
      ) {
        return true;
      }
      return state;
    case IMPORT_STATE:
      return action.payload.present.everPublished === undefined
        ? state
        : action.payload.present.everPublished;
    default:
      return state;
  }
}
// A scene created AFTER the author set the global "Scene defaults" has to be
// born with them too. The per-slice reducer that stamps a new viewToolbar
// (view_toolbars_by_id) cannot see `globalConfig` — it is a sibling slice — so
// the merge happens here, one level up, where the whole undoGroup is in scope.
//
// Done as a post-pass over the combined reducer rather than in the action
// creators on purpose: a scene is added from a dozen call sites (carousel,
// templates, ZIP import, AI generation, …) and threading the defaults through
// each one is exactly how they came to disagree in the first place.
//
// Only from-scratch creation. Duplicate/paste deliberately inherit the scene
// they were copied from.
const NEW_SCENE_ACTIONS = [ADD_NAV_ITEM, ADD_NAV_ITEMS];

function applySceneDefaultsToNewScenes(next, prev, action) {
  if (!NEW_SCENE_ACTIONS.includes(action.type)) return next;
  const defaults = sceneDefaultsFromGlobalConfig(next.globalConfig);
  if (!defaults) return next;

  const prevToolbars = (prev && prev.viewToolbarsById) || {};
  let toolbars = next.viewToolbarsById;
  let patched = null;
  Object.keys(toolbars).forEach((id) => {
    if (prevToolbars[id]) return; // already existed — not ours to touch
    if (!patched) patched = { ...toolbars };
    patched[id] = { ...toolbars[id], ...defaults };
  });
  return patched ? { ...next, viewToolbarsById: patched } : next;
}

const undoGroupSlices = combineReducers({
  version: versionReducer,
  lastActionDispatched: lastActionDispatched,
  globalConfig: globalConfig,
  boxesById: boxesById, // {0: box0, 1: box1}
  boxSelectedAll: boxSelectedAll,
  boxSelected: boxSelected, // 0
  boxLevelSelected: boxLevelSelected, // 0
  indexSelected: indexSelected,
  marksById: marksById, // {0: mark1, 1: mark2}
  navItemsIds: navItemsIds, // [0, 1]
  navItemSelected: navItemSelected, // 0
  navItemsById: navItemsById, // {0: navItem0, 1: navItem1}
  containedViewsById: containedViewsById, // {0: containedView0, 1: containedView1}
  containedViewSelected: containedViewSelected, // 0
  displayMode: changeDisplayMode, // "list",
  uploadedTemplates: uploadTemplate, // {0: template0 , 1 : template1}
  pluginToolbarsById: pluginToolbarsById, // {0: toolbar0, 1: toolbar1}
  viewToolbarsById: viewToolbarsById,
  exercises: exercises,
  soundList: addSound,
  animationsById: addAnimation,
  isBusy: isBusy,
});

function undoGroupReducer(state, action = {}) {
  return keepBrandAssetsOnTop(
    applySceneDefaultsToNewScenes(undoGroupSlices(state, action), state, action),
    state
  );
}

const GlobalState = combineReducers({
  filesUploaded, // We are not allowed to undo file uploads/removals because the files remain in the server
  guideTagValues,
  status,
  everPublished,
  aideaSmartElementsProcessing, // Ephemeral, non-undoable; tracks in-flight magic-element-engine jobs by source boxId
  workflowSuggestions, // === Task 37 — Ephemeral, non-undoable; active WorkflowSuggestionList row ===
  interactivityStudio, // === Interactivity Studio #4 Task 1 — Ephemeral, non-undoable; unified Studio open/tab/busy ===
  canvasWorkflow, // === Phase B Task 4 — Ephemeral, non-undoable; docked Workflow panel visibility ===
  canvasPreview, // === Phase C item 6 — Ephemeral, non-undoable; workflow-node preview frame for canvas hard-hide ===
  userTemplates, // === Save-as-template — non-undoable; user-defined Interactive templates persisted to localStorage ===
  undoGroup: undoable(undoGroupReducer, {
    // A crop = several dispatches (PASTE_BOX + DELETE_BOX + resize). Without
    // grouping, each is its own undo step, so a single Ctrl+Z only reverts the
    // last one and the original photo never comes back cleanly. handleCropToNewBox
    // stamps window.cropUndoGroup with a unique id for the duration of the op;
    // redux-undo collapses all same-group actions into ONE history entry, so one
    // Ctrl+Z undoes the whole crop. null outside a crop = normal per-action steps.
    // window.undoGroupId does the same for a dialog session (e.g. Şekil
    // ayarları: every live edit while it is open is one Ctrl+Z).
    groupBy: (action) => window.cropUndoGroup || window.undoGroupId || null,
    filter: (action, currentState, previousState) => {
      /* flag for */
      if (action.payload && action.payload.reason && action.payload.reason === "saving_state") {
        window.exitFlag = true;
      } else {
        window.exitFlag = null;
      }

      // Bulk infographic animation apply (product ruling): explicitly
      // non-revertable, excluded from undo history entirely. It dispatches
      // the same shared ADD_BOX/DELETE_BOX/UPDATE_PLUGIN_TOOLBAR action
      // creators normal single-image edits use, which must stay
      // undo-tracked — so this can't be a type-based case below.
      // See _editor/utils/applyBulkAnimationResults.es6.
      if (window.bulkAnimateApplying) {
        return false;
      }

      switch (action.type) {
        // case CHANGE_DISPLAY_MODE:
        case EXPAND_NAV_ITEM:
        case IMPORT_STATE:
        case INCREASE_LEVEL:
        case INDEX_SELECT:
        case SELECT_BOX:
        case SELECT_NAV_ITEM:
        case SELECT_CONTAINED_VIEW:
        case SET_BUSY:
        case TOGGLE_TEXT_EDITOR:
        case UPLOAD_FILE:
        case DELETE_FILE:
        case UPLOAD_TEMPLATE:
        case DELETE_TEMPLATE:
          // case UPDATE_BOX:
          // case ADD_RICH_MARK:
          return false;
      }

      if (action.type === ADD_BOX) {
        if (action.payload.initialParams && action.payload.initialParams.isDefaultPlugin) {
          return false;
        } else if (isSortableBox(action.payload.ids.id)) {
          return false;
        }
      }
      // TODO: Comprobar que esto funciona
      return currentState !== previousState;
    },
  }),
});

export default GlobalState;
